import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { MapPin, Loader2, Check, X, Navigation, Plane, Search } from "lucide-react";

/** HomeBaseModal — agent picks the airport they're travelling FROM.
 *
 *  Three input paths, all converge on a real IATA airport record:
 *    1. "Use my current location" → browser geolocation → backend geo-lookup → nearest airport.
 *    2. Typeahead search (any city, e.g. "Valencia") → backend geocodes via Nominatim → nearest airport.
 *    3. Browse the full airport list as a fallback.
 *
 *  Persists `home_airport_iata` on the user record so subsequent trips auto-fill.
 */
export default function HomeBaseModal({ open, initialCity, onConfirm, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);          // [{iata, city, name, country, distance_km, match_kind, geocoded_display}]
  const [searching, setSearching] = useState(false);
  const [pick, setPick] = useState(null);              // chosen airport object
  const [allAirports, setAllAirports] = useState([]);  // fallback list
  const [showAll, setShowAll] = useState(false);
  const [geoState, setGeoState] = useState("idle");    // idle | locating | resolved | denied | error
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Reset state every time the modal opens; preload current home base if any.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    setPick(null);
    setGeoState("idle");
    setShowAll(false);
    api.get("/travel/home-base").then(({ data }) => {
      setAllAirports(data?.airports || []);
      if (data?.airport) setPick(data.airport);
      else if (initialCity) {
        // Fallback: try to find airport by saved city name
        const ap = (data?.airports || []).find((a) => a.city.toLowerCase() === String(initialCity).toLowerCase());
        if (ap) setPick(ap);
      }
    }).catch(() => {});
    // Focus the input after the next frame so click-to-type works immediately
    requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
  }, [open, initialCity]);

  // Debounced typeahead → /travel/airport-search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/travel/airport-search", { query: query.trim() });
        setResults(data?.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { setGeoState("error"); return; }
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post("/travel/geo-lookup", {
            lat: pos.coords.latitude, lng: pos.coords.longitude,
          });
          if (data?.nearest_airport) {
            setPick(data.nearest_airport);
            setQuery(data.nearest_airport.city);
            setGeoState("resolved");
          } else { setGeoState("error"); }
        } catch { setGeoState("error"); }
      },
      () => setGeoState("denied"),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  const save = async () => {
    if (!pick) return;
    setSaving(true);
    try {
      await api.put("/travel/home-base", { home_city: pick.city, home_iata: pick.iata });
      onConfirm && onConfirm({ home_city: pick.city, home_iata: pick.iata, airport: pick });
    } catch {
      onConfirm && onConfirm({ home_city: pick.city, home_iata: pick.iata, airport: pick });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const visibleResults = results.length > 0 ? results : (showAll ? allAirports.map((a) => ({ ...a, match_kind: "local" })) : []);

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      data-testid="home-base-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[88vh]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Plane size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Travelling from</div>
            <div className="font-display text-lg font-bold tracking-tight truncate">Pick your home airport</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="home-base-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto pm-scroll">
          {/* Geolocation shortcut */}
          <button
            onClick={useMyLocation}
            disabled={geoState === "locating"}
            className="w-full h-11 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-400 text-emerald-800 text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-60"
            data-testid="home-base-use-location"
          >
            {geoState === "locating" ? <Loader2 size={13} className="animate-spin" /> : <Navigation size={13} />}
            {geoState === "locating" ? "Locating…" : "Use my current location"}
          </button>

          {/* Typeahead input */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">Or type any city, airport, or IATA</div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. Valencia · Lisbon · MUC"
                className="pm-input w-full text-sm h-11 pl-9"
                data-testid="home-base-search-input"
                autoComplete="off"
                spellCheck={false}
              />
              {searching && (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="home-base-show-all"
              className="text-[11px] text-slate-500 hover:text-slate-700 font-bold mt-1.5"
            >
              {showAll ? "Hide full list" : "Or browse all supported airports"}
            </button>
          </div>

          {/* Results */}
          {visibleResults.length > 0 && (
            <ul className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-72 overflow-y-auto pm-scroll" data-testid="home-base-results">
              {visibleResults.map((r, i) => {
                const selected = pick?.iata === r.iata;
                return (
                  <li key={`${r.iata}-${i}`}>
                    <button
                      onClick={() => setPick(r)}
                      data-testid={`home-base-result-${r.iata}`}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${selected ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-display font-black ${selected ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700"}`}>
                        {r.iata}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-slate-900 truncate">
                          {r.city}{r.country ? `, ${r.country}` : ""}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {r.name}
                          {typeof r.distance_km === "number" && r.distance_km > 0 ? <span> · ≈ {r.distance_km} km</span> : null}
                        </div>
                      </div>
                      {r.match_kind === "geocoded" && (
                        <span className="pm-pill pm-pill-emerald text-[9px]" data-testid={`match-kind-geocoded-${r.iata}`}>Nearest</span>
                      )}
                      {selected && <Check size={14} className="text-emerald-700 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {geoState === "denied" && (
            <div className="text-[11px] text-amber-700">Location permission denied — type your city above.</div>
          )}
          {geoState === "error" && (
            <div className="text-[11px] text-rose-700">Couldn't fetch location — type your city above.</div>
          )}

          {/* Chosen summary */}
          {pick && (
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/40 p-3 flex items-center gap-3" data-testid="home-base-pick">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-xs font-display font-black">
                {pick.iata}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">Selected airport</div>
                <div className="font-bold text-sm truncate">{pick.city}{pick.country ? `, ${pick.country}` : ""} · {pick.name}</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2 bg-slate-50 shrink-0">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" data-testid="home-base-cancel">Cancel</button>
          <div className="flex-1" />
          <button
            onClick={save}
            disabled={!pick || saving}
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="home-base-confirm"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Use {pick?.iata || "—"} & plan trip
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
